/**
 * Ticket View Page
 * ================
 * View ticket details with attachments, comments timeline, and status controls.
 */

import { useState, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  MessageSquare,
  Clock,
  LinkIcon,
  Send,
  Download,
  X,
  Upload,
  FileText,
  Image,
  FileSpreadsheet,
  Archive,
  File as FileIcon,
  ClipboardCheck,
} from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import {
  useTicket,
  useUpdateTicket,
  useDeleteTicket,
  useAddTicketComment,
  useUploadTicketAttachment,
  useDeleteTicketAttachment,
} from '@/hooks/useTickets'
import { ticketApi } from '@/services/api'
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_PRIORITY_DOT_COLORS,
  TICKET_CATEGORY_LABELS,
  TICKET_SOURCE_LABELS,
} from '@/types'
import type { TicketStatus, TicketPriority } from '@/types'

const STATUS_TRANSITIONS: { value: TicketStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'waiting_external', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
]

const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString()
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return <Image size={16} className="text-blue-500" />
  if (mimetype.includes('spreadsheet') || mimetype.includes('excel') || mimetype === 'text/csv')
    return <FileSpreadsheet size={16} className="text-green-500" />
  if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('7z') || mimetype.includes('gzip'))
    return <Archive size={16} className="text-yellow-500" />
  if (mimetype.includes('pdf') || mimetype.includes('word') || mimetype.includes('document') || mimetype.startsWith('text/'))
    return <FileText size={16} className="text-red-500" />
  return <FileIcon size={16} className="text-gray-500" />
}

export default function TicketView() {
  const navigate = useNavigate()
  const { ticketId } = useParams<{ ticketId: string }>()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState<TicketStatus | null>(null)
  const [statusNote, setStatusNote] = useState('')
  const [commentText, setCommentText] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: ticketData, isLoading } = useTicket(ticketId ?? '')
  const updateTicket = useUpdateTicket()
  const deleteTicket = useDeleteTicket()
  const addComment = useAddTicketComment()
  const uploadAttachment = useUploadTicketAttachment()
  const deleteAttachment = useDeleteTicketAttachment()

  const ticket = ticketData?.data

  // Statuses that prompt for resolution notes
  const NOTE_STATUSES: TicketStatus[] = ['resolved', 'closed']

  const handleStatusClick = (newStatus: TicketStatus) => {
    if (!ticket || ticket.status === newStatus) return
    if (NOTE_STATUSES.includes(newStatus)) {
      setStatusNote('')
      setShowStatusModal(newStatus)
    } else {
      handleStatusChange(newStatus)
    }
  }

  const handleStatusChange = async (newStatus: TicketStatus, note?: string) => {
    if (!ticket) return
    await updateTicket.mutateAsync({
      ticketId: ticket.ticket_id,
      data: { status: newStatus, ...(note ? { status_note: note } : {}) },
    })
  }

  const handleStatusModalConfirm = async () => {
    if (!showStatusModal) return
    await handleStatusChange(showStatusModal, statusNote.trim() || undefined)
    setShowStatusModal(null)
    setStatusNote('')
  }

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    if (!ticket) return
    await updateTicket.mutateAsync({
      ticketId: ticket.ticket_id,
      data: { priority: newPriority },
    })
  }

  const handleDelete = async () => {
    if (!ticket) return
    try {
      await deleteTicket.mutateAsync(ticket.ticket_id)
      navigate('/tickets')
    } catch (error) {
      console.error('Failed to delete ticket:', error)
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ticket || !commentText.trim()) return
    try {
      await addComment.mutateAsync({
        ticketId: ticket.ticket_id,
        data: { content: commentText.trim() },
      })
      setCommentText('')
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  // Attachment upload handlers
  const handleUploadFiles = useCallback(async (files: File[]) => {
    if (!ticket) return
    setUploadError(null)

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setUploadError(`File "${file.name}" is too large (${formatFileSize(file.size)}). Max ${MAX_FILE_SIZE_MB}MB.`)
        continue
      }
      try {
        await uploadAttachment.mutateAsync({ ticketId: ticket.ticket_id, file })
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err)
        setUploadError(`Failed to upload "${file.name}". The file may be too large or have an unsupported type.`)
      }
    }
  }, [ticket, uploadAttachment])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    handleUploadFiles(Array.from(e.dataTransfer.files))
  }, [handleUploadFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUploadFiles(Array.from(e.target.files))
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [handleUploadFiles])

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!ticket) return
    try {
      await deleteAttachment.mutateAsync({ ticketId: ticket.ticket_id, attachmentId })
    } catch (error) {
      console.error('Failed to delete attachment:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading ticket...</p>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Ticket not found</p>
        <Button variant="secondary" onClick={() => navigate('/tickets')} className="mt-4">
          Back to Tickets
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tickets')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${TICKET_STATUS_COLORS[ticket.status]}`}>
              {TICKET_STATUS_LABELS[ticket.status]}
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{ticket.title}</h1>
              <p className="text-gray-500 dark:text-gray-400">
                {ticket.ticket_id} • Created {formatRelative(ticket.created_at)}
                {ticket.source !== 'manual' && ` • via ${TICKET_SOURCE_LABELS[ticket.source]}`}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            icon={<Edit size={16} />}
            onClick={() => navigate(`/tickets/${ticket.ticket_id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 size={16} />}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Ticket?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{ticket.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleteTicket.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal (for Resolved/Closed) */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <ClipboardCheck size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {showStatusModal === 'closed' ? 'Close Ticket' : 'Resolve Ticket'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {showStatusModal === 'closed'
                    ? 'Closing this ticket — anything to note before wrapping up?'
                    : 'Nice work! What was done to resolve this?'}
                </p>
              </div>
            </div>
            <textarea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Describe what was done to fix or address this issue... (optional)"
              rows={4}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 resize-none mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => { setShowStatusModal(null); setStatusNote('') }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusModalConfirm}
                loading={updateTicket.isPending}
              >
                {statusNote.trim()
                  ? (showStatusModal === 'closed' ? 'Close with Notes' : 'Resolve with Notes')
                  : (showStatusModal === 'closed' ? 'Close Ticket' : 'Mark Resolved')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader title="Description" />
            <CardContent>
              {ticket.description ? (
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {ticket.description}
                </p>
              ) : (
                <p className="text-gray-400 dark:text-gray-500 italic">No description provided.</p>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card>
            <CardHeader
              title="Attachments"
              action={
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {ticket.attachments?.length ?? 0} file{(ticket.attachments?.length ?? 0) !== 1 ? 's' : ''}
                </span>
              }
            />
            <CardContent>
              {/* File list */}
              {ticket.attachments && ticket.attachments.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {ticket.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg group"
                    >
                      {getFileIcon(att.mimetype)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{att.filename}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {formatFileSize(att.file_size)} • {formatRelative(att.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={ticketApi.getAttachmentDownloadUrl(ticket.ticket_id, att.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                          title="Download"
                        >
                          <Download size={14} />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          title="Delete attachment"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 dark:text-gray-500 text-sm italic mb-4">
                  No attachments yet.
                </p>
              )}

              {/* Upload drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                {uploadAttachment.isPending ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Uploading...</span>
                ) : (
                  <>
                    <Upload size={16} className={dragActive ? 'text-primary-500' : 'text-gray-400'} />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-primary-600 dark:text-primary-400">Click to upload</span> or drag files here
                    </span>
                  </>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />

              {uploadError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">{uploadError}</p>
              )}
            </CardContent>
          </Card>

          {/* Comments / Activity Timeline */}
          <Card>
            <CardHeader
              title="Activity"
              action={
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {ticket.comments.length} {ticket.comments.length === 1 ? 'entry' : 'entries'}
                </span>
              }
            />
            <CardContent>
              {ticket.comments.length === 0 ? (
                <p className="text-gray-400 dark:text-gray-500 text-sm italic">
                  No activity yet. Add a comment below.
                </p>
              ) : (
                <div className="space-y-4">
                  {ticket.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`flex gap-3 ${
                        comment.comment_type === 'status_change'
                          ? 'items-center'
                          : 'items-start'
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          comment.comment_type === 'status_change'
                            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                            : comment.comment_type === 'system'
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            : 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400'
                        }`}
                      >
                        {comment.comment_type === 'status_change' ? (
                          <Clock size={14} />
                        ) : (
                          <MessageSquare size={14} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {comment.comment_type === 'status_change' ? (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Status changed from{' '}
                            <span className="font-medium">{TICKET_STATUS_LABELS[comment.old_status as TicketStatus] ?? comment.old_status}</span>
                            {' '}to{' '}
                            <span className="font-medium">{TICKET_STATUS_LABELS[comment.new_status as TicketStatus] ?? comment.new_status}</span>
                            <span className="text-gray-400 dark:text-gray-500 ml-2">
                              {formatRelative(comment.created_at)}
                            </span>
                          </p>
                        ) : (
                          <>
                            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                              {comment.content}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {formatDate(comment.created_at)}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add comment form */}
              <form onSubmit={handleAddComment} className="mt-6 pt-4 border-t dark:border-gray-700">
                <div className="flex gap-2">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    icon={<Send size={14} />}
                    disabled={!commentText.trim()}
                    loading={addComment.isPending}
                  >
                    Send
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Control */}
          <Card>
            <CardHeader title="Status" />
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {STATUS_TRANSITIONS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => handleStatusClick(s.value)}
                    disabled={ticket.status === s.value || updateTicket.isPending}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      ticket.status === s.value
                        ? TICKET_STATUS_COLORS[s.value] + ' ring-2 ring-offset-1 ring-gray-300 dark:ring-gray-600'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    } disabled:opacity-50`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Priority Control */}
          <Card>
            <CardHeader title="Priority" />
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {PRIORITY_OPTIONS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => handlePriorityChange(p.value)}
                    disabled={ticket.priority === p.value || updateTicket.isPending}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      ticket.priority === p.value
                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white ring-2 ring-offset-1 ring-gray-300 dark:ring-gray-500'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    } disabled:opacity-50`}
                  >
                    <span className={`w-2 h-2 rounded-full ${TICKET_PRIORITY_DOT_COLORS[p.value]}`} />
                    {p.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader title="Details" />
            <CardContent className="space-y-3 text-sm">
              <div>
                <label className="block text-gray-500 dark:text-gray-400">Category</label>
                <p className="text-gray-900 dark:text-white">
                  {ticket.category ? TICKET_CATEGORY_LABELS[ticket.category] : 'None'}
                </p>
              </div>
              <div>
                <label className="block text-gray-500 dark:text-gray-400">Source</label>
                <p className="text-gray-900 dark:text-white">
                  {TICKET_SOURCE_LABELS[ticket.source]}
                </p>
              </div>
              {ticket.source_ref && (
                <div>
                  <label className="block text-gray-500 dark:text-gray-400">Source Reference</label>
                  <p className="text-gray-900 dark:text-white font-mono text-xs break-all">
                    {ticket.source_ref}
                  </p>
                </div>
              )}
              {ticket.asset && (
                <div>
                  <label className="block text-gray-500 dark:text-gray-400">Linked Asset</label>
                  <button
                    onClick={() => navigate(`/assets/${ticket.asset!.asset_id}`)}
                    className="flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <LinkIcon size={14} />
                    {ticket.asset.name}
                  </button>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {ticket.asset.asset_id} - {ticket.asset.asset_type}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader title="Timestamps" />
            <CardContent className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Clock size={14} />
                <span>Created: {formatDate(ticket.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={14} />
                <span>Updated: {formatDate(ticket.updated_at)}</span>
              </div>
              {ticket.resolved_at && (
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>Resolved: {formatDate(ticket.resolved_at)}</span>
                </div>
              )}
              {ticket.closed_at && (
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>Closed: {formatDate(ticket.closed_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
